import React, { useRef, useState, useCallback } from 'react';
import RecipePageStarRatingStaticStar from "@/ReactInit/Scripts/RecipePage/components/StarRatingStaticStar";

interface Recipe {
    id: string;
    title: string;
    imageUrl: string;
    link: string;
    altText: string;
    preparationTime: string;
    averageRating: number;
    numberOfRatings: number;
}
interface WeekMenuProps {
    data: {
        activeIndex: number;
        recipes: Recipe[];
    };
}

const WeekMenuCarousel: React.FC<WeekMenuProps> = ({ data }) => {
    const recipes = data.recipes;
    const [activeIndex, setActiveIndex] = useState(data.activeIndex);
    const length = recipes.length;

    const visibleCount = 5; 
    const centerPosition = Math.floor(visibleCount / 2);

    const getOrder = (index: number) =>
        (index - activeIndex + centerPosition + length) % length;

    const prev = useCallback(
        () => setActiveIndex((i) => (i - 1 + length) % length),
        [length]
    );
    const next = useCallback(
        () => setActiveIndex((i) => (i + 1) % length),
        [length]
    );
    
    const start = useRef({ x: 0, y: 0, dragging: false, pointerId: -1 });
    const moved = useRef(false);
    const gestureType = useRef<"none" | "pointer" | "touch">("none");
    const THRESHOLD = 24;

    // Handle click if we have swiped
    const cancelClickIfDragged = (e: React.MouseEvent | React.TouchEvent) => {
        if (moved.current) {
            e.preventDefault();
            e.stopPropagation();
        }
    };

    //Touch (iOS Safari)
    const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
        // Hvis pointer allerede aktiv, ignorer touch
        if (gestureType.current === "pointer") return;
        gestureType.current = "touch";
        const t = e.touches[0];
        start.current = { x: t.clientX, y: t.clientY, dragging: true, pointerId: -1 };
        moved.current = false;
    };

    const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
        if (gestureType.current !== "touch" || !start.current.dragging) return;
        const t = e.touches[0];
        const dx = t.clientX - start.current.x;
        const dy = t.clientY - start.current.y;
        if (!moved.current && (Math.abs(dx) > 3 || Math.abs(dy) > 3)) moved.current = true;
        
        if (Math.abs(dx) > Math.abs(dy)) {
            e.stopPropagation();
        }
    };

    const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
        if (gestureType.current !== "touch" || !start.current.dragging) return;
        const t = e.changedTouches[0];
        const dx = t.clientX - start.current.x;
        const dy = t.clientY - start.current.y;
        start.current.dragging = false;
        gestureType.current = "none";

        if (Math.abs(dx) < THRESHOLD || Math.abs(dx) <= Math.abs(dy)) return;
        if (dx < 0) next();
        else prev();
    };

    const handleTouchCancel = () => {
        start.current.dragging = false;
        gestureType.current = "none";
    };

    return (
        <div className="w-full relative">
            {/*Previous button*/}
            <button
                onClick={prev}
                className="btn-primary btnPrev !p-2 z-10 rounded-full absolute -left-4 top-1/3 -translate-y-1/2"
                aria-label="Forrige oppskrift"
            >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M15 18L9 12L15 6" stroke="#222222" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>

            <div className="w-full mx-auto mt-6 flex flex-col items-center justify-center relative">
                <div className="flex items-center w-full relative">
                    {/* Carousel */}
                    <div
                        className="flex items-center justify-center w-full gap-5"
                        style={{ touchAction: 'pan-y', userSelect: 'none', overflow: 'hidden' }}
                        aria-roledescription="carousel"
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        onTouchCancel={handleTouchCancel}
                    >
                        {recipes.map((recipe, i) => {
                            const distance = (i - activeIndex + length) % length;
                            if (
                                distance > Math.floor(visibleCount / 2) &&
                                distance < length - Math.floor(visibleCount / 2)
                            ) {
                                return null;
                            }

                            const order = getOrder(i);
                            const isActive = i === activeIndex;

                            const W_ACTIVE   = "w-[250px] tablet:w-[250px] laptop:w-[300px] desktop:w-[300px]";
                            const W_INACTIVE = "w-[193px] tablet:w-[193px] laptop:w-[225px] desktop:w-[277px]";
                            const mediaWidth = isActive ? W_ACTIVE : W_INACTIVE;
                            const H_MEDIA = "h-[175px] tablet:h-[175px] laptop:h-[210px] desktop:h-[210px]";

                            return (
                                <div
                                    key={recipe.id}
                                    className={`group relative flex flex-col items-center cursor-pointer flex-none"`}
                                    style={{ order }}
                                    onClickCapture={cancelClickIfDragged}
                                >
                                    <div className={`flex items-center justify-center ${H_MEDIA} ${mediaWidth}`}>
                                        <figure className={`aspect-[7/5] ${mediaWidth} overflow-hidden rounded-xl`} style={{ aspectRatio: "7 / 5" }}>                                               
                                            <picture>
                                                <source media="(min-width: 1280px)" srcSet={`${recipe.imageUrl}__w=300_h=210_autocrop=true`} />
                                                <source media="(min-width: 1024px)" srcSet={`${recipe.imageUrl}__w=300_h=210_autocrop=true`} />
                                                <source media="(min-width: 768px)"  srcSet={`${recipe.imageUrl}__w=250_h=175_autocrop=true`} />
                                                <img
                                                    className="block w-full h-full object-cover transition-transform ease-in-out transform-gpu group-hover:scale-110 will-change-transform"
                                                    src={`${recipe.imageUrl}__w=250_h=175_autocrop=true`}
                                                    alt={recipe.altText}
                                                    draggable={false}
                                                    decoding="async"
                                                />
                                                </picture>
                                        </figure>
                                    </div>

                                    <a
                                        href={recipe.link}
                                        className="absolute inset-0 z-5"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={cancelClickIfDragged}
                                    >
                                        <span className="sr-only">{recipe.title}</span>
                                    </a>

                                    {/*Title*/}
                                    <div className="text-left w-full">
                                        <div className="min-h-[3.5rem] laptop:min-h-[4rem] flex items-center">
                                            <h2
                                                className={`font-semibold text-xl laptop:text-2xl !leading-tight mb-2 ${
                                                    isActive ? "line-clamp-1" : "line-clamp-2"
                                                }`}
                                            >
                                                {recipe.title}
                                            </h2>
                                        </div>

                                        {/*Star rating*/}
                                        <ul className="flex items-center justify-start mb-2">
                                            {Array.from({ length: 5 }, (_, i) => (
                                                <li key={i}>
                                                    <RecipePageStarRatingStaticStar starValue={recipe.averageRating} starIndex={i}/>
                                                </li>
                                            ))}
                                            <li className='mx-1 font-semibold text-sm'>
                                                {recipe.averageRating}
                                            </li>
                                            <li className='mx-1 text-sm text-black-muted'>
                                                ({recipe.numberOfRatings})
                                            </li>
                                        </ul>

                                        {/*Preparation time*/}
                                        <div>
                                            <p className="text-sm flex gap-1 items-center">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                    <path d="M12 6V12L16 14M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="#BE9164" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                                {recipe.preparationTime}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                </div>
            </div>

            {/* Next button */}
            <button
                onClick={next}
                className="btn-primary btnNext !p-2 z-10 rounded-full absolute -right-4 top-1/3 -translate-y-1/2"
                aria-label="Neste oppskrift"
            >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M9 5L16 12L9 19" stroke="#00020B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>
        </div>
    );
};

export default WeekMenuCarousel;
